import React, { useEffect, useMemo, useState } from "react";
import { FiSearch, FiX, FiFileText, FiPlus } from "react-icons/fi";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { getAuth } from "../../services/localStorage";
import { API_ENDPOINT } from "../../services/api";
import { sanitizeImageUrl } from "../../utils/sanitizeImageUrl";

const PAGE_SIZE_OPTIONS = [5, 10, 20];

const EMPTY_FORM = {
  registration: "",
  date_arrival: "",
  description: "",
  supporting_image: null,
  supporting_video: null,
  supporting_document: null,
};

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString();
}

function fileLabel(file) {
  if (!file) return "No file chosen";
  return file.name || "1 file selected";
}

function matchesSearch(item, query) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    item?.registration,
    item?.description,
    item?.date_arrival,
    item?.status,
  ]
    .filter((v) => v != null && v !== "")
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

function Arbitration() {
  const [tabIdx, setTabIdx] = useState(0);
  const [existingData, setExistingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const auth = getAuth();
  const isLoading = Boolean(loading) && existingData.length === 0;

  const fetchCases = async () => {
    if (!auth?.token) {
      setLoading(false);
      setExistingData([]);
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINT("api/v1/arbitrations/"), {
        headers: {
          Authorization: `Token ${auth.token}`,
        },
      });
      const json = await response.json();
      const rows = Array.isArray(json?.data)
        ? json.data
        : Array.isArray(json)
        ? json
        : [];
      setExistingData(rows);
    } catch {
      setExistingData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, sortBy, pageSize, tabIdx]);

  const filteredList = useMemo(() => {
    let rows = [...existingData].filter((item) => matchesSearch(item, search));
    rows.sort((a, b) => {
      const ta = new Date(a?.date_arrival || 0).getTime() || 0;
      const tb = new Date(b?.date_arrival || 0).getTime() || 0;
      if (sortBy === "oldest") return ta - tb;
      if (sortBy === "reg_asc") {
        return String(a?.registration || "").localeCompare(
          String(b?.registration || "")
        );
      }
      if (sortBy === "reg_desc") {
        return String(b?.registration || "").localeCompare(
          String(a?.registration || "")
        );
      }
      return tb - ta;
    });
    return rows;
  }, [existingData, search, sortBy]);

  const pageCount = Math.max(1, Math.ceil(filteredList.length / pageSize) || 1);
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, filteredList.length);
  const pageRows = filteredList.slice(pageStart, pageEnd);
  const hasActiveFilters = Boolean(search) || sortBy !== "newest";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    if (submitError) setSubmitError("");
  };

  const handleFileUpload = (e, type) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({
      ...prev,
      [`supporting_${type}`]: file,
    }));
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setErrors({});
    setSubmitError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = {};
    if (!formData.registration.trim()) {
      validationErrors.registration = "Vehicle registration is required";
    }
    if (!formData.date_arrival) {
      validationErrors.date_arrival = "Arrival date is required";
    }
    if (!formData.description.trim()) {
      validationErrors.description = "Description is required";
    }
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;
    if (!auth?.token || !auth?.id) {
      setSubmitError("Please sign in again to submit a case.");
      return;
    }

    setSubmitting(true);
    setSubmitError("");
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("registration", formData.registration.trim());
      formDataToSend.append("date_arrival", formData.date_arrival);
      formDataToSend.append("user_id", auth.id);
      formDataToSend.append("description", formData.description.trim());
      if (formData.supporting_image) {
        formDataToSend.append("supporting_image", formData.supporting_image);
      }
      if (formData.supporting_video) {
        formDataToSend.append("supporting_video", formData.supporting_video);
      }
      if (formData.supporting_document) {
        formDataToSend.append(
          "supporting_document",
          formData.supporting_document
        );
      }

      const response = await fetch(API_ENDPOINT("api/v1/arbitrations/"), {
        method: "POST",
        headers: {
          Authorization: `Token ${auth.token}`,
        },
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error("Submit failed");
      }

      resetForm();
      await fetchCases();
      setTabIdx(0);
    } catch {
      setSubmitError("Could not submit the case. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dd-section">
      <nav className="dd-tabs" aria-label="Arbitration sections">
        <button
          type="button"
          className={`dd-tab${tabIdx === 0 ? " is-active" : ""}`}
          onClick={() => setTabIdx(0)}
        >
          Cases
          <span className="dd-tab__count">{existingData.length}</span>
        </button>
        <button
          type="button"
          className={`dd-tab${tabIdx === 1 ? " is-active" : ""}`}
          onClick={() => setTabIdx(1)}
        >
          New case
        </button>
      </nav>

      {tabIdx === 1 ? (
        <form className="dd-form dd-arb-form" onSubmit={handleSubmit} noValidate>
          <div className="dd-arb-intro">
            <span className="dd-arb-intro__badge" aria-hidden>
              <FiFileText size={18} />
            </span>
            <div>
              <h2>Raise a trade dispute</h2>
              <p>
                Share the registration, arrival date, and what went wrong. Add
                media if you have it — our team will follow up shortly.
              </p>
            </div>
          </div>

          <div className="dd-form-grid">
            <div
              className={`dd-field${
                errors.registration ? " is-invalid" : ""
              }`}
            >
              <label htmlFor="registration">Vehicle registration *</label>
              <input
                type="text"
                name="registration"
                id="registration"
                className="form-control"
                placeholder="e.g. AB12 CDE"
                value={formData.registration}
                onChange={handleChange}
                autoComplete="off"
              />
              {errors.registration ? (
                <p className="dd-password__error">{errors.registration}</p>
              ) : null}
            </div>

            <div
              className={`dd-field${
                errors.date_arrival ? " is-invalid" : ""
              }`}
            >
              <label htmlFor="date_arrival">Date of arrival *</label>
              <input
                type="date"
                name="date_arrival"
                id="date_arrival"
                className="form-control"
                value={formData.date_arrival}
                onChange={handleChange}
              />
              {errors.date_arrival ? (
                <p className="dd-password__error">{errors.date_arrival}</p>
              ) : null}
            </div>

            <div
              className={`dd-field dd-field--full${
                errors.description ? " is-invalid" : ""
              }`}
            >
              <label htmlFor="description">Describe the issue *</label>
              <textarea
                name="description"
                id="description"
                className="form-control"
                rows={4}
                placeholder="What happened, and what outcome are you looking for?"
                value={formData.description}
                onChange={handleChange}
              />
              {errors.description ? (
                <p className="dd-password__error">{errors.description}</p>
              ) : null}
            </div>

            <div className="dd-field">
              <label htmlFor="supporting_image">Supporting image</label>
              <input
                id="supporting_image"
                type="file"
                className="form-control"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handleFileUpload(e, "image")}
              />
              <p className="dd-arb-file-hint">
                {fileLabel(formData.supporting_image)}
              </p>
            </div>

            <div className="dd-field">
              <label htmlFor="supporting_video">Supporting video</label>
              <input
                id="supporting_video"
                type="file"
                className="form-control"
                accept="video/*"
                onChange={(e) => handleFileUpload(e, "video")}
              />
              <p className="dd-arb-file-hint">
                {fileLabel(formData.supporting_video)}
              </p>
            </div>

            <div className="dd-field">
              <label htmlFor="supporting_document">Supporting document</label>
              <input
                id="supporting_document"
                type="file"
                className="form-control"
                accept=".pdf,application/pdf"
                onChange={(e) => handleFileUpload(e, "document")}
              />
              <p className="dd-arb-file-hint">
                {fileLabel(formData.supporting_document)}
              </p>
            </div>
          </div>

          {submitError ? (
            <p className="dd-password__error" role="alert">
              {submitError}
            </p>
          ) : null}

          <div className="dd-btn-row">
            <button
              type="button"
              className="dd-btn dd-btn--ghost"
              disabled={submitting}
              onClick={() => {
                resetForm();
                setTabIdx(0);
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="dd-btn dd-btn--primary"
              disabled={submitting}
            >
              {submitting ? "Submitting…" : "Submit case"}
            </button>
          </div>
        </form>
      ) : isLoading ? (
        <div className="dd-empty" aria-busy="true">
          <strong>Loading cases…</strong>
          <p>Fetching arbitration records for your dealer desk.</p>
        </div>
      ) : (
        <>
          <div className="dd-toolbar">
            <label className="dd-toolbar__search">
              <FiSearch size={16} aria-hidden />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search reg, issue…"
                aria-label="Search arbitration cases"
              />
              {search ? (
                <button
                  type="button"
                  className="dd-toolbar__clear-input"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  <FiX size={14} />
                </button>
              ) : null}
            </label>

            <label className="dd-toolbar__field">
              <span>Sort</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="newest">Arrival · newest</option>
                <option value="oldest">Arrival · oldest</option>
                <option value="reg_asc">Reg · A–Z</option>
                <option value="reg_desc">Reg · Z–A</option>
              </select>
            </label>

            <label className="dd-toolbar__field">
              <span>Per page</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>

            {hasActiveFilters ? (
              <button
                type="button"
                className="dd-btn dd-btn--ghost"
                onClick={() => {
                  setSearch("");
                  setSortBy("newest");
                  setPage(1);
                }}
              >
                Reset
              </button>
            ) : null}

            <button
              type="button"
              className="dd-btn dd-btn--primary"
              onClick={() => setTabIdx(1)}
            >
              <FiPlus size={16} aria-hidden />
              New case
            </button>
          </div>

          <div className="dd-toolbar__meta">
            {filteredList.length ? (
              <span>
                Showing {pageStart + 1}–{pageEnd} of {filteredList.length}
                {filteredList.length !== existingData.length
                  ? ` (filtered from ${existingData.length})`
                  : ""}
              </span>
            ) : (
              <span>No matching cases</span>
            )}
          </div>

          {!existingData.length ? (
            <div className="dd-empty">
              <strong>No arbitration cases</strong>
              <p>
                Create a case when you need help resolving a trade or delivery
                issue.
              </p>
              <button
                type="button"
                className="dd-btn dd-btn--primary"
                style={{ marginTop: "0.75rem" }}
                onClick={() => setTabIdx(1)}
              >
                <FiPlus size={16} aria-hidden />
                Create case
              </button>
            </div>
          ) : !filteredList.length ? (
            <div className="dd-empty">
              <strong>No matches</strong>
              <p>Try a different search or reset filters.</p>
            </div>
          ) : (
            <>
              <div className="dd-table-wrap dd-arb-table">
                <table className="dd-table">
                  <thead>
                    <tr>
                      <th scope="col">Registration</th>
                      <th scope="col">Arrival</th>
                      <th scope="col">Issue</th>
                      <th scope="col">Evidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((item, index) => {
                      const image = sanitizeImageUrl(item?.supporting_image);
                      return (
                        <tr key={item?.id || `${item?.registration}-${index}`}>
                          <td>
                            <span className="dd-arb-reg">
                              {String(item?.registration || "—").toUpperCase()}
                            </span>
                          </td>
                          <td>{formatDate(item?.date_arrival)}</td>
                          <td>
                            <div className="dd-arb-issue">
                              {item?.description || "—"}
                            </div>
                          </td>
                          <td>
                            <div className="dd-arb-evidence">
                              {image ? (
                                <a
                                  href={image}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="dd-arb-evidence__thumb"
                                  title="Open image"
                                >
                                  <img src={image} alt="" />
                                </a>
                              ) : null}
                              {item?.supporting_video ? (
                                <a
                                  href={item.supporting_video}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="dd-action"
                                >
                                  Video
                                </a>
                              ) : null}
                              {item?.supporting_document ? (
                                <a
                                  href={item.supporting_document}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="dd-action"
                                >
                                  Document
                                </a>
                              ) : null}
                              {!image &&
                              !item?.supporting_video &&
                              !item?.supporting_document
                                ? "—"
                                : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {filteredList.length > pageSize ? (
                <div className="dd-pagination">
                  <PaginationControl
                    page={safePage}
                    between={3}
                    total={filteredList.length}
                    limit={pageSize}
                    changePage={(next) => setPage(next)}
                    ellipsis={1}
                  />
                </div>
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default Arbitration;
